import {
  FormArchiveMutationDTO,
  FormArchivePreviewDTO,
  FormEditSessionDiscardMutationDTO,
  FormKPIPlacementMutationDTO,
  FormKPIPlacementResultDTO,
  FormPurgePreviewDTO,
  FormPurgeMutationDTO,
  FormStructureMutationDTO,
  FormStructureMutationResultDTO,
  FormTextCorrectionMutationDTO,
  FormUpgradeMutationDTO,
  FormUpgradePreviewDTO,
  FormUpgradeResultDTO,
  FormUpgradeTaskDTO,
  TextCorrectionImpactPreviewDTO,
  FormVersionDiffDTO,
  FormVersionHistoryDTO,
} from 'models/dto/tasks/formVersionDTO';
import {
  FormKPIPlacementResult,
  FormArchivePreview,
  FormArchiveProducer,
  FormPurgePreview,
  FormStructureMutationResult,
  FormTextCorrectionMutation,
  FormUpgradePreview,
  FormUpgradeResult,
  FormUpgradeTask,
  FormVersionAmendment,
  FormVersionDiff,
  FormVersionFieldDiff,
  FormVersionHistory,
  TextCorrectionFormVersionImpact,
  TextCorrectionImpactPreview,
  TextCorrectionTaskStateImpact,
} from 'models/tasks/formVersion';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import KPI from 'models/kpi/kpi';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { mapFromKPI, mapToKPI } from 'mappings/kpiMapping';
import { mapFromTaskType, mapToTaskType, mapToTaskTypeForm } from 'mappings/taskTypeMapping';

export function mapToFormStructureMutation(
  taskType: TaskType,
  formElements: TaskTypeFormElement[],
  mutationId: string,
  editSessionId: string,
): FormStructureMutationDTO {
  const output = new FormStructureMutationDTO();
  output.taskTypeFamilyId = taskType.taskTypeFamilyId ?? 0;
  output.expectedTaskTypeId = taskType.taskTypeId;
  output.expectedHeadRowVersion = taskType.rowVersion;
  output.mutationId = mutationId;
  output.editSessionId = editSessionId;
  output.form = mapToTaskType(taskType);
  output.form.form = mapToTaskTypeForm(formElements);

  return output;
}

export function mapToFormEditSessionDiscardMutation(
  taskType: TaskType,
  baselineDefinitionHash: string,
  mutationId: string,
  editSessionId: string,
): FormEditSessionDiscardMutationDTO {
  const output = new FormEditSessionDiscardMutationDTO();
  output.taskTypeFamilyId = taskType.taskTypeFamilyId ?? 0;
  output.expectedTaskTypeId = taskType.taskTypeId;
  output.expectedHeadRowVersion = taskType.rowVersion;
  output.baselineDefinitionHash = baselineDefinitionHash;
  output.expectedCurrentDefinitionHash = taskType.definitionHash ?? '';
  output.mutationId = mutationId;
  output.editSessionId = editSessionId;

  return output;
}

export function mapFromFormStructureMutationResult(dto: FormStructureMutationResultDTO): FormStructureMutationResult {
  const output = new FormStructureMutationResult();
  output.taskType = mapFromTaskType(dto.taskType);
  output.isNoOp = dto.isNoOp;
  output.createdVersion = dto.createdVersion;

  return output;
}

export function mapToFormKPIPlacementMutation(
  kpi: KPI,
  taskType: TaskType,
  formElements: TaskTypeFormElement[],
  newFieldIndex: number,
  mutationId: string,
  editSessionId: string,
): FormKPIPlacementMutationDTO {
  const output = new FormKPIPlacementMutationDTO();
  output.kpi = mapToKPI(kpi);
  output.form = mapToFormStructureMutation(taskType, formElements, mutationId, editSessionId);
  output.newFieldIndex = newFieldIndex;

  return output;
}

export function mapFromFormKPIPlacementResult(
  dto: FormKPIPlacementResultDTO,
  cache: GlobalDataCache | undefined,
): FormKPIPlacementResult {
  const output = new FormKPIPlacementResult();
  output.kpi = mapFromKPI(dto.kpi, cache);
  output.form = mapFromFormStructureMutationResult(dto.form);

  return output;
}

export function mapFromFormVersionHistory(dto: FormVersionHistoryDTO): FormVersionHistory {
  const output = new FormVersionHistory();
  output.taskTypeFamilyId = dto.taskTypeFamilyId;
  output.taskTypeId = dto.taskTypeId;
  output.version = dto.version;
  output.name = dto.name;
  output.date = new Date(dto.date);
  output.changedById = dto.changedById;
  output.changedBy = dto.changedBy;
  output.numberOfFields = dto.numberOfFields;
  output.isCurrent = dto.isCurrent;
  output.isUsed = dto.isUsed;
  output.taskCount = dto.taskCount ?? 0;
  output.amendments = (dto.amendments ?? []).map((amendment) => {
    const mapped = new FormVersionAmendment();
    mapped.formVersionEventId = amendment.formVersionEventId;
    mapped.eventType = amendment.eventType;
    mapped.actorKind = amendment.actorKind;
    mapped.date = new Date(amendment.date);
    mapped.changedById = amendment.changedById;
    mapped.changedBy = amendment.changedBy;
    mapped.triggeredById = amendment.triggeredById;
    mapped.triggeredBy = amendment.triggeredBy;
    mapped.beforeHash = amendment.beforeHash;
    mapped.afterHash = amendment.afterHash;
    mapped.changedProperties = amendment.changedProperties;
    mapped.hasSnapshotAnchor = amendment.hasSnapshotAnchor;

    return mapped;
  });

  return output;
}

export function mapFromFormVersionDiff(dto: FormVersionDiffDTO): FormVersionDiff {
  const output = new FormVersionDiff();
  output.taskTypeFamilyId = dto.taskTypeFamilyId;
  output.taskTypeId = dto.taskTypeId;
  output.compareTaskTypeId = dto.compareTaskTypeId;
  output.fields = dto.fields.map((field) => {
    const mapped = new FormVersionFieldDiff();
    mapped.fieldKey = field.fieldKey;
    mapped.fieldId = field.fieldId;
    mapped.status = field.status;
    mapped.changedProperties = field.changedProperties;

    return mapped;
  });

  return output;
}

export function mapFromFormArchivePreview(dto: FormArchivePreviewDTO): FormArchivePreview {
  const output = new FormArchivePreview();
  output.taskTypeFamilyId = dto.taskTypeFamilyId;
  output.producers = dto.producers.map((producer) => {
    const mapped = new FormArchiveProducer();
    mapped.taskId = producer.taskId;
    mapped.name = producer.name;
    mapped.taskType = producer.taskType;

    return mapped;
  });
  output.inaccessibleProducerCount = dto.inaccessibleProducerCount;
  output.blockingConcreteTaskCount = dto.blockingConcreteTaskCount;

  return output;
}

// The archive dialog is the confirmation: templates, series masters and event headers bound to the
// family are detached with the archive, so the mutation sets the detach flag.
export function mapToFormArchiveMutation(taskTypeFamilyId: number): FormArchiveMutationDTO {
  const output = new FormArchiveMutationDTO();
  output.taskTypeFamilyId = taskTypeFamilyId;
  output.detachProducers = true;

  return output;
}

function mapFromFormUpgradeTasks(dtos: FormUpgradeTaskDTO[] | undefined): FormUpgradeTask[] {
  return (dtos ?? []).map((dto) => {
    const mapped = new FormUpgradeTask();
    mapped.taskId = dto.taskId;
    mapped.name = dto.name;

    return mapped;
  });
}

export function mapFromFormUpgradePreview(dto: FormUpgradePreviewDTO): FormUpgradePreview {
  const output = new FormUpgradePreview();
  output.taskTypeFamilyId = dto.taskTypeFamilyId;
  output.taskMasterId = dto.taskMasterId;
  output.taskTypeId = dto.taskTypeId;
  output.version = dto.version;
  output.name = dto.name;
  output.tasks = mapFromFormUpgradeTasks(dto.tasks);
  output.upgradableCount = dto.upgradableCount;
  output.answeredCount = dto.answeredCount;
  output.closedCount = dto.closedCount;
  output.inaccessibleCount = dto.inaccessibleCount;

  return output;
}

export function mapFromFormUpgradeResult(dto: FormUpgradeResultDTO): FormUpgradeResult {
  const output = new FormUpgradeResult();
  output.taskTypeFamilyId = dto.taskTypeFamilyId;
  output.taskMasterId = dto.taskMasterId;
  output.taskTypeId = dto.taskTypeId;
  output.version = dto.version;
  output.name = dto.name;
  output.upgraded = mapFromFormUpgradeTasks(dto.upgraded);
  output.upgradedCount = dto.upgradedCount;
  output.answeredCount = dto.answeredCount;
  output.closedCount = dto.closedCount;
  output.inaccessibleCount = dto.inaccessibleCount;

  return output;
}

export function mapToFormUpgradeMutation(taskTypeFamilyId: number, taskMasterId?: number): FormUpgradeMutationDTO {
  const output = new FormUpgradeMutationDTO();
  output.taskTypeFamilyId = taskTypeFamilyId;
  output.taskMasterId = taskMasterId;

  return output;
}

export function mapToFormPurgeMutation(
  taskTypeFamilyId: number,
  taskTypeId: number,
  mutationId: string,
): FormPurgeMutationDTO {
  const output = new FormPurgeMutationDTO();
  output.taskTypeFamilyId = taskTypeFamilyId;
  output.taskTypeId = taskTypeId;
  output.mutationId = mutationId;

  return output;
}

export function mapToFormTextCorrectionMutation(mutation: FormTextCorrectionMutation): FormTextCorrectionMutationDTO {
  const output = new FormTextCorrectionMutationDTO();
  output.taskTypeFamilyId = mutation.taskTypeFamilyId;
  output.taskTypeId = mutation.taskTypeId;
  output.fieldKey = mutation.fieldKey;
  output.languageId = mutation.languageId;
  output.name = mutation.name;
  output.description = mutation.description;
  output.tabName = mutation.tabName;
  output.instruction = mutation.instruction;
  output.expectedResult = mutation.expectedResult;
  output.expectedTaskTypeRowVersion = mutation.expectedTaskTypeRowVersion;
  output.expectedImpactHash = mutation.expectedImpactHash;
  output.mutationId = mutation.mutationId;

  return output;
}

export function mapFromTextCorrectionImpactPreview(dto: TextCorrectionImpactPreviewDTO): TextCorrectionImpactPreview {
  const output = new TextCorrectionImpactPreview();
  output.impactHash = dto.impactHash;
  output.affectedFamilyCount = dto.affectedFamilyCount;
  output.affectedTaskCount = dto.affectedTaskCount;
  output.affectedKPIDataCount = dto.affectedKPIDataCount;
  output.draftReportCount = dto.draftReportCount;
  output.publishedReportCount = dto.publishedReportCount;
  output.formVersions = dto.formVersions.map((version) => {
    const mapped = new TextCorrectionFormVersionImpact();
    mapped.taskTypeFamilyId = version.taskTypeFamilyId;
    mapped.taskTypeId = version.taskTypeId;
    mapped.version = version.version;
    mapped.name = version.name;
    mapped.isCurrent = version.isCurrent;
    mapped.isArchived = version.isArchived;
    mapped.taskCount = version.taskCount;

    return mapped;
  });
  output.taskStates = dto.taskStates.map((state) => {
    const mapped = new TextCorrectionTaskStateImpact();
    mapped.taskStateId = state.taskStateId;
    mapped.taskCount = state.taskCount;

    return mapped;
  });

  return output;
}

export function mapFromFormPurgePreview(dto: FormPurgePreviewDTO): FormPurgePreview {
  const output = new FormPurgePreview();
  output.taskTypeFamilyId = dto.taskTypeFamilyId;
  output.taskTypeId = dto.taskTypeId;
  output.canPurge = dto.canPurge;
  output.blockers = dto.blockers;

  return output;
}
